import * as Primitive from "@radix-ui/react-toast";
import { create } from "zustand";

export interface ToastAction {
  label: string;
  onClick: () => void;
}

interface ToastItem {
  id: number;
  title: string;
  description?: string;
  tone: "info" | "error";
  action?: ToastAction;
  /** ms; 0 keeps it until dismissed. */
  duration?: number;
}

interface ToastStore {
  toasts: ToastItem[];
  push: (toast: Omit<ToastItem, "id" | "tone"> & { tone?: ToastItem["tone"] }) => void;
  dismiss: (id: number) => void;
}

let nextId = 1;

export const useToastStore = create<ToastStore>((set) => ({
  toasts: [],
  push: (toast) =>
    set((s) => ({ toasts: [...s.toasts, { id: nextId++, tone: "info", ...toast }] })),
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}));

type ToastOptions = { action?: ToastAction; duration?: number };

export const toast = {
  info: (title: string, description?: string, options?: ToastOptions) =>
    useToastStore.getState().push({ title, description, ...options }),
  error: (title: string, description?: string, options?: ToastOptions) =>
    useToastStore.getState().push({ title, description, tone: "error", ...options }),
};

/** Titles and descriptions are always rendered as text (file names may be hostile). */
export function Toaster() {
  const toasts = useToastStore((s) => s.toasts);
  const dismiss = useToastStore((s) => s.dismiss);
  return (
    <Primitive.Provider swipeDirection="right">
      {toasts.map((t) => (
        <Primitive.Root
          key={t.id}
          duration={t.duration ?? (t.tone === "error" ? 8000 : t.action ? 8000 : 4000)}
          onOpenChange={(open) => !open && dismiss(t.id)}
          className="rounded-md border border-border bg-bg p-3 shadow-lg"
        >
          <Primitive.Title
            className={t.tone === "error" ? "font-medium text-danger" : "font-medium"}
          >
            {t.title}
          </Primitive.Title>
          {t.description ? (
            <Primitive.Description className="whitespace-pre-line text-sm text-muted">
              {t.description}
            </Primitive.Description>
          ) : null}
          {t.action ? (
            <Primitive.Action altText={t.action.label} asChild>
              <button
                type="button"
                className="mt-2 rounded border border-border px-2 py-1 text-sm font-medium text-primary hover:bg-surface"
                onClick={t.action.onClick}
              >
                {t.action.label}
              </button>
            </Primitive.Action>
          ) : null}
        </Primitive.Root>
      ))}
      <Primitive.Viewport className="fixed bottom-4 left-4 z-50 flex w-96 max-w-[calc(100vw-2rem)] flex-col gap-2" />
    </Primitive.Provider>
  );
}
